import React from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { PoweredByTrustChatIcon } from "../widgetPreviewIcons";
import { COLORS, FONT } from "../../../shared/styles";
import {
  normalizePreChatFields,
  resolveWidgetPreviewState,
} from "../constants";
import WidgetInputBar from "./WidgetInputBar";
import WidgetFab from "./WidgetFab";
import WidgetPreviewHeader from "./WidgetPreviewHeader";
import widgetAvatar from "../../../../../../assets/images/widget-avatar.png";

const AGENT_AVATAR = widgetAvatar;

const formInputSx = {
  width: "100%",
  mt: "8px",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    borderRadius: "8px",
    fontFamily: FONT.family,
    fontSize: "14px",
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    background: COLORS.white,
    "& fieldset": { borderColor: "#B7C2CC" },
  },
  "& .MuiOutlinedInput-input": { py: 0, px: "12px" },
};

const formTextareaSx = {
  width: "100%",
  mt: "8px",
  "& .MuiOutlinedInput-root": {
    minHeight: "45px",
    borderRadius: "8px",
    fontFamily: FONT.family,
    fontSize: "14px",
    boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
    background: COLORS.white,
    alignItems: "flex-start",
    py: "8px",
    "& fieldset": { borderColor: "#B7C2CC" },
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
  },
};

const labelSx = {
  mt: "12px",
  fontSize: "12px",
  fontFamily: FONT.family,
  color: COLORS.textSecondary,
};

function ChatHeader({ primaryColor }) {
  return (
    <Box
      sx={{
        height: 64,
        px: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: primaryColor,
        borderRadius: "8px 8px 0 0",
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundImage: `url(${AGENT_AVATAR})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundColor: "rgba(255,255,255,0.25)",
          }}
        />
        <Typography sx={{ color: COLORS.white, fontSize: "16px", fontWeight: 600, lineHeight: "20px" }}>
          Eleanor Pena
        </Typography>
      </Box>
      <Typography sx={{ color: COLORS.white, fontSize: "20px", lineHeight: 1, letterSpacing: "2px" }}>
        ⋯
      </Typography>
    </Box>
  );
}

function PreChatFormPreview({
  primaryColor,
  allowGuest,
  greetingText,
  greetingSubtitle,
  preChatFields,
}) {
  const fields = normalizePreChatFields(preChatFields).filter((field) => field.enabled);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 368,
        mt: "-56px",
        borderRadius: "8px",
        background: "linear-gradient(218deg, rgba(255, 255, 255, 0.80) 1.96%, #FFF 106.88%)",
        boxShadow: "0 8px 24px rgba(6, 21, 34, 0.12)",
        p: "20px",
        backdropFilter: "blur(20px)",
      }}
    >
      <Typography
        sx={{
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "18px",
          fontWeight: 500,
          lineHeight: "22px",
          textAlign: "center",
        }}
      >
        {greetingText || "Hi there! 👋"}
      </Typography>
      <Typography
        sx={{
          mt: "4px",
          color: COLORS.textSecondary,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "18px",
          textAlign: "center",
        }}
      >
        {greetingSubtitle || "Please Fill in the form below before start the chat"}
      </Typography>

      {fields.map((field) => (
        <React.Fragment key={field.id}>
          <Typography sx={labelSx}>
            {field.label}
            {field.required ? (
              <Box component="span" sx={{ color: COLORS.error }}>
                *
              </Box>
            ) : null}
          </Typography>
          <TextField placeholder={`Enter ${String(field.label || "value").toLowerCase()}`} sx={formInputSx} />
        </React.Fragment>
      ))}

      <Button
        fullWidth
        disableElevation
        sx={{
          mt: "16px",
          height: "40px",
          borderRadius: "8px",
          background: primaryColor,
          color: COLORS.white,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { background: primaryColor, opacity: 0.92 },
        }}
      >
        Start the chat
      </Button>
      {allowGuest ? (
        <Button
          fullWidth
          disableElevation
          sx={{
            mt: "8px",
            height: "40px",
            borderRadius: "8px",
            border: `1px solid ${COLORS.border}`,
            background: COLORS.white,
            color: COLORS.textPrimary,
            fontFamily: FONT.family,
            fontSize: "14px",
            fontWeight: 500,
            textTransform: "none",
            "&:hover": { background: COLORS.white },
          }}
        >
          Start chat as a Guest
        </Button>
      ) : null}
    </Box>
  );
}

function OfflineTicketFormPreview({ primaryColor }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 368,
        mt: "-56px",
        borderRadius: "8px",
        background: COLORS.white,
        boxShadow: "0 8px 24px rgba(6, 21, 34, 0.12)",
        p: "20px",
      }}
    >
      <Typography
        sx={{
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "18px",
          fontWeight: 500,
          lineHeight: "22px",
          textAlign: "center",
        }}
      >
        No agent available
      </Typography>
      <Typography
        sx={{
          mt: "4px",
          color: COLORS.textSecondary,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "18px",
          textAlign: "center",
        }}
      >
        Please fill in the form below to leave a message
      </Typography>

      <Typography sx={labelSx}>
        Name:<Box component="span" sx={{ color: COLORS.error }}>*</Box>
      </Typography>
      <TextField placeholder="Enter name" sx={formInputSx} />

      <Typography sx={labelSx}>
        Email:<Box component="span" sx={{ color: COLORS.error }}>*</Box>
      </Typography>
      <TextField placeholder="Enter email address" sx={formInputSx} />
      <Typography sx={labelSx}>
        Subject:<Box component="span" sx={{ color: COLORS.error }}>*</Box>
      </Typography>
      <TextField placeholder="Enter subject" sx={formInputSx} />
      <Typography sx={labelSx}>Description</Typography>
      <TextField
        placeholder="Describe your issue"
        multiline
        minRows={3}
        sx={formTextareaSx}
      />

      <Button
        fullWidth
        disableElevation
        sx={{
          mt: "16px",
          height: "40px",
          borderRadius: "8px",
          background: primaryColor,
          color: COLORS.white,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { background: primaryColor, opacity: 0.92 },
        }}
      >
        Send message for support team
      </Button>
    </Box>
  );
}

function DirectChatPreview({ primaryColor, welcomeMessage }) {
  const agentMessage = welcomeMessage?.trim() || "Hello. How may I help you?";

  return (
    <>
      <ChatHeader primaryColor={primaryColor} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          p: "16px",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              flexShrink: 0,
              backgroundImage: `url(${AGENT_AVATAR})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundColor: "#E5E9EC",
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <Box
              sx={{
                maxWidth: "100%",
                px: "12px",
                py: "8px",
                borderRadius: "1px 16px 16px 16px",
                background: "rgba(241, 243, 245, 0.80)",
                backdropFilter: "blur(5px)",
                color: COLORS.textPrimary,
                fontFamily: FONT.family,
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              {agentMessage}
            </Box>
            <Typography
              sx={{
                mt: "4px",
                fontSize: "11px",
                fontFamily: FONT.family,
                color: COLORS.textSecondary,
              }}
            >
              01:34 pm
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: "auto", width: "100%" }}>
          <Button
            fullWidth
            disableElevation
            sx={{
              height: "40px",
              borderRadius: "8px",
              background: primaryColor,
              color: COLORS.white,
              fontFamily: FONT.family,
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: primaryColor, opacity: 0.92 },
            }}
          >
            Chat now
          </Button>
          <Box sx={{ display: "flex", justifyContent: "center", pt: "16px" }}>
            <PoweredByTrustChatIcon />
          </Box>
        </Box>
      </Box>
    </>
  );
}

function FormWidgetShell({ primaryColor, logo, children, hideInputBar }) {
  return (
    <Box sx={{ width: 400 }}>
      <Box
        sx={{
          width: 400,
          height: 619,
          borderRadius: "8px",
          background: COLORS.white,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxShadow: "0 8px 24px rgba(6, 21, 34, 0.12)",
        }}
      >
        <WidgetPreviewHeader primaryColor={primaryColor} logo={logo} />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: "24px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {children}

          <Box sx={{ mt: "auto", pb: "10px", pt: "16px" }}>
            <PoweredByTrustChatIcon />
          </Box>
        </Box>

        {!hideInputBar && <WidgetInputBar />}
      </Box>
    </Box>
  );
}

function HiddenWidgetPreview() {
  return (
    <Box sx={{ width: 400 }}>
      <Box
        sx={{
          width: 400,
          height: 619,
          borderRadius: "8px",
          border: `1px dashed ${COLORS.border}`,
          background: "#F8FAFC",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: "32px",
        }}
      >
        <Typography
          sx={{
            color: COLORS.textSecondary,
            fontFamily: FONT.family,
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: "20px",
            textAlign: "center",
          }}
        >
          Widget is hidden when agents are offline
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Single reusable widget preview for Appearance and Behaviour tabs.
 */
function WidgetPreview({ settings, agentsOnline = true }) {
  const {
    primaryColor,
    position,
    preChatForm,
    startChatAlways,
    showWhenOffline,
    welcomeMessage,
    greetingText,
    greetingSubtitle,
    logo,
    allowGuest,
    preChatFields,
  } = settings;

  const { visible, mode } = resolveWidgetPreviewState({
    agentsOnline,
    preChatForm,
    startChatAlways,
    showWhenOffline,
  });

  if (!visible || mode === "hidden") {
    return <HiddenWidgetPreview />;
  }

  if (mode === "ticket") {
    return (
      <>
        <FormWidgetShell primaryColor={primaryColor} logo={logo} hideInputBar>
          <OfflineTicketFormPreview primaryColor={primaryColor} />
        </FormWidgetShell>
        <WidgetFab color={primaryColor} position={position} variant="chat" />
      </>
    );
  }

  if (mode === "direct_chat") {
    return (
      <Box sx={{ width: 400 }}>
        <Box
          sx={{
            width: 400,
            height: 619,
            borderRadius: "8px",
            background: COLORS.white,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 24px rgba(6, 21, 34, 0.12)",
          }}
        >
          <DirectChatPreview primaryColor={primaryColor} welcomeMessage={welcomeMessage} />
        </Box>
        <WidgetFab color={primaryColor} position={position} variant="chat" />
      </Box>
    );
  }

  return (
    <>
      <FormWidgetShell primaryColor={primaryColor} logo={logo} hideInputBar>
        <PreChatFormPreview
          primaryColor={primaryColor}
          allowGuest={allowGuest}
          greetingText={greetingText}
          greetingSubtitle={greetingSubtitle}
          preChatFields={preChatFields}
        />
      </FormWidgetShell>
      <WidgetFab color={primaryColor} position={position} variant="chat" />
    </>
  );
}

export default React.memo(WidgetPreview);
